/**
 * Focus timer.
 *
 * The timer runs client-side off a wall-clock deadline rather than by
 * decrementing a counter each tick: a background tab throttles intervals to
 * once a second at best, and often far less, so a decrementing counter drifts
 * badly over a 25-minute period. Storing the deadline and deriving the
 * remaining time on each tick keeps it accurate no matter how the tab is
 * scheduled.
 *
 * Periods started against a Dashplan block log their minutes back onto that
 * block, which is what makes planned-vs-actual work.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Play, Pause, RotateCcw, SkipForward, CalendarClock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { todayKey, formatDuration, formatMinute } from "@/lib/dates";
import { KINDS, type DashplanKind } from "@/components/dashplan/constants";

const DEFAULT_TEMPLATE = [
  { type: "align" as const, duration: 2 },
  { type: "focus" as const, duration: 25 },
  { type: "break" as const, duration: 5 },
  { type: "focus" as const, duration: 25 },
  { type: "break" as const, duration: 5 },
  { type: "reflect" as const, duration: 3 },
];

const PERIOD_LABEL: Record<string, string> = {
  align: "Align",
  focus: "Focus",
  break: "Break",
  reflect: "Reflect",
};

const NO_BLOCK = "__none__";

export default function FocusPage() {
  const { toast } = useToast();

  const sessions = useQuery(api.focus.listSessions, {});
  const todaysPlan = useQuery(api.dashplan.day, { date: todayKey() });
  const logPeriod = useMutation(api.focus.log);
  const logToBlock = useMutation(api.dashplan.logFocus);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [blockId, setBlockId] = useState<string>(NO_BLOCK);
  const [periodIndex, setPeriodIndex] = useState(0);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [pausedRemaining, setPausedRemaining] = useState<number | null>(null);
  const [, forceTick] = useState(0);

  const template = useMemo(() => {
    const chosen = sessions?.find((s) => s._id === sessionId);
    return chosen?.template ?? DEFAULT_TEMPLATE;
  }, [sessions, sessionId]);

  const period = template[periodIndex] ?? template[0];
  const running = deadline !== null;

  // Re-render every second while running so the countdown updates. The
  // remaining time is always derived from the deadline, never accumulated.
  useEffect(() => {
    if (!running) return;
    const interval = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(interval);
  }, [running]);

  const remainingMs = running
    ? Math.max(0, deadline - Date.now())
    : (pausedRemaining ?? period.duration * 60_000);

  const finishPeriod = useCallback(
    async (completed: boolean) => {
      setDeadline(null);
      setPausedRemaining(null);

      if (completed && (period.type === "focus" || period.type === "break")) {
        try {
          if (blockId !== NO_BLOCK && period.type === "focus") {
            // Logging against a block also writes the focus log row, so this
            // is one call rather than two.
            await logToBlock({
              id: blockId as Id<"dashplanEntries">,
              minutes: period.duration,
            });
          } else {
            await logPeriod({
              duration: period.duration,
              type: period.type,
              loggedDate: todayKey(),
            });
          }
        } catch (error) {
          toast({
            title: "Could not save that period",
            description: String(error),
            variant: "destructive",
          });
        }
      }

      setPeriodIndex((index) => (index + 1) % template.length);
    },
    [period, blockId, logToBlock, logPeriod, template.length, toast],
  );

  // Fire the completion exactly once when the deadline passes.
  const finishedRef = useRef(false);
  useEffect(() => {
    if (!running) {
      finishedRef.current = false;
      return;
    }
    if (remainingMs > 0 || finishedRef.current) return;

    finishedRef.current = true;
    void finishPeriod(true);
  }, [running, remainingMs, finishPeriod]);

  const start = () => {
    const ms = pausedRemaining ?? period.duration * 60_000;
    setDeadline(Date.now() + ms);
    setPausedRemaining(null);
  };

  const pause = () => {
    if (deadline === null) return;
    setPausedRemaining(Math.max(0, deadline - Date.now()));
    setDeadline(null);
  };

  const reset = () => {
    setDeadline(null);
    setPausedRemaining(null);
  };

  const totalMs = period.duration * 60_000;
  const progress = totalMs === 0 ? 0 : ((totalMs - remainingMs) / totalMs) * 100;

  const minutes = Math.floor(remainingMs / 60_000);
  const seconds = Math.floor((remainingMs % 60_000) / 1000);

  const selectedBlock = todaysPlan?.find((e) => e._id === blockId);

  return (
    <div className="space-y-5 max-w-2xl">
      <h1 className="page-title text-2xl font-semibold">Focus</h1>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="text-center space-y-1">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">
              {PERIOD_LABEL[period.type] ?? period.type}
            </p>
            <p
              className={cn(
                "text-6xl font-bold font-mono tabular-nums",
                period.type === "focus" && "text-primary",
              )}
            >
              {String(minutes).padStart(2, "0")}:
              {String(seconds).padStart(2, "0")}
            </p>
            <p className="text-xs text-muted-foreground">
              Period {periodIndex + 1} of {template.length}
            </p>
          </div>

          <Progress value={progress} />

          <div className="flex justify-center gap-2">
            {running ? (
              <Button onClick={pause} size="lg" variant="outline">
                <Pause className="w-4 h-4 mr-1.5" />
                Pause
              </Button>
            ) : (
              <Button onClick={start} size="lg">
                <Play className="w-4 h-4 mr-1.5" />
                {pausedRemaining !== null ? "Resume" : "Start"}
              </Button>
            )}
            <Button onClick={reset} size="lg" variant="ghost" aria-label="Reset">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => void finishPeriod(false)}
              size="lg"
              variant="ghost"
              aria-label="Skip this period"
            >
              <SkipForward className="w-4 h-4" />
            </Button>
          </div>

          {/* Period strip */}
          <div className="flex gap-1">
            {template.map((p, index) => (
              <div
                key={index}
                title={`${PERIOD_LABEL[p.type] ?? p.type} · ${p.duration}m`}
                className={cn(
                  "flex-1 h-1.5 rounded-full transition-colors",
                  index < periodIndex
                    ? "bg-primary/40"
                    : index === periodIndex
                      ? "bg-primary"
                      : "bg-muted",
                )}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Session</CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={sessionId ?? "__default__"}
              onValueChange={(value) => {
                setSessionId(value === "__default__" ? null : value);
                setPeriodIndex(0);
                reset();
              }}
            >
              <SelectTrigger aria-label="Focus session template">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">
                  Classic Pomodoro (default)
                </SelectItem>
                {(sessions ?? []).map((session) => (
                  <SelectItem key={session._id} value={session._id}>
                    {session.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" />
              Log against a block
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Select value={blockId} onValueChange={setBlockId}>
              <SelectTrigger aria-label="Dashplan block">
                <SelectValue placeholder="Not linked" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_BLOCK}>Not linked</SelectItem>
                {(todaysPlan ?? []).map((entry) => (
                  <SelectItem key={entry._id} value={entry._id}>
                    {formatMinute(entry.startMinute)} · {entry.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedBlock ? (
              <BlockSummary entry={selectedBlock} />
            ) : (
              <p className="text-xs text-muted-foreground">
                Pick a block from today's Dashplan and finished focus periods
                count towards it.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function BlockSummary({
  entry,
}: {
  entry: {
    title: string;
    kind: string;
    durationMinutes: number;
    actualFocusMinutes: number;
  };
}) {
  const kind = KINDS[entry.kind as DashplanKind] ?? KINDS.other;
  const Icon = kind.icon;
  const over = entry.actualFocusMinutes > entry.durationMinutes;

  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className={cn("w-3.5 h-3.5 shrink-0", kind.accent)} />
      <span className="truncate flex-1">{entry.title}</span>
      <span
        className={cn(
          "font-mono tabular-nums shrink-0",
          over ? "text-amber-400" : "text-muted-foreground",
        )}
        title="Actual focus vs. planned"
      >
        {formatDuration(entry.actualFocusMinutes)} /{" "}
        {formatDuration(entry.durationMinutes)}
      </span>
    </div>
  );
}
